import { Injectable } from '@angular/core';
import { ToastService } from '@shared/toast/services/toast.service';
import { ConfirmationService } from '@shared/confirmation/services/confirmation.service';
import { Id } from '@shared/types/id';
import { map, Observable, of, tap } from 'rxjs';
import { SequenceDetailsConverter } from '@features/sequence/converters/sequence-details.converter';
import { Sequence } from '@features/sequence/models/sequence';
import { FileActionService } from '@features/file/services/file-action.service';
import { blank } from '@core/utils/rxjs/blank';
import { SequenceUpsertFormData } from '@features/sequence/components/sequence-upsert-form/models/sequence-upsert-form-data';
import { SequenceCreateRequest } from '@api/sequence/requests/sequence-create.request';
import { SequenceApiService } from '@api/sequence/sequence-api.service';

@Injectable({
  providedIn: 'root',
})
export class SequenceActionService {
  constructor(
    private readonly sequenceApi: SequenceApiService,
    private readonly detailsConverter: SequenceDetailsConverter,
    private readonly toast: ToastService,
    private readonly confirmation: ConfirmationService,
    private readonly fileAction: FileActionService
  ) {}

  fetch(id: Id): Observable<Sequence> {
    return this.sequenceApi
      .details(id)
      .pipe(map(response => this.detailsConverter.fromResponse(response)));
  }

  createByForm(data: SequenceUpsertFormData): Observable<void> {
    const request = new SequenceCreateRequest(
      data.name,
      data.description,
      data.generatedAt,
      data.file.id
    );

    return this.sequenceApi.create(request).pipe(
      tap({
        next: () =>
          this.toast.show(builder =>
            builder.initSuccess('Sekwencja została dodana pomyślnie.').build()
          ),
        error: () =>
          this.toast.show(builder =>
            builder.initError('Nie udało się utworzyć sekwencji.').build()
          ),
      }),
      blank()
    );
  }

  downloadFasta(url: string, name: string): Observable<void> {
    return of(this.fileAction.downloadAsBlob(url, name)).pipe(
      tap({
        error: () =>
          this.toast.show(builder =>
            builder.initError('Nie udało się pobrać pliku.').build()
          ),
      })
    );
  }

  downloadAb1(id: Id, fileName: string): Observable<string> {
    return this.fileAction.previewUrl(id).pipe(
      tap(url => this.fileAction.download(url, fileName)),
      tap({
        error: () =>
          this.toast.show(builder =>
            builder.initError('Nie udało się pobrać pliku.').build()
          ),
      })
    );
  }

  confirmAndRemove(id: Id): Observable<void> {
    return new Observable<void>(subscriber => {
      this.confirmation.show(builder =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć sekwencję?')
          .accept(() => this.remove(id).subscribe(subscriber))
          .reject(() => subscriber.complete())
          .build()
      );
    });
  }

  private remove(id: Id): Observable<void> {
    return this.sequenceApi.remove(id).pipe(
      tap({
        next: () =>
          this.toast.show(builder =>
            builder.initSuccess('Sekwencja została usunięta pomyślnie.').build()
          ),
        error: () =>
          this.toast.show(builder =>
            builder.initError('Nie udało się usunąć sekwencji.').build()
          ),
      })
    );
  }
}
